import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { api, AttendedEventMemberSummary } from '../../services/api';
import { AttendanceMapMode, SessionAttendeesMap } from '../../components/map';
import SessionEditAttendanceDialog from './SessionEditAttendanceDialog';

/**
 * Admin: attendees for a single training session.
 *
 * Displays one row per (CEA) with clock-in / clock-out timestamps, plus:
 *   • Edit Attendance → opens the session-scoped edit dialog.
 *
 * Export is available from the session actions column in the Events Overview
 * sessions table. The underlying data is filtered by
 * `TransformationTrainingSessionId` server-side (see
 * `fetchAttendedMembersForSession`).
 */

interface SessionAttendeesDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: number | null;
  sessionName: string;
  /** YYYY-MM-DD; used by the edit dialog to combine with time-of-day. */
  sessionDate: string;
  /** Retained for call-site compatibility; export now lives in SessionsPanel. */
  eventId: string;
  eventName: string;
  /** When true, show the scan-locations map (admin only). */
  isAdmin?: boolean;
  /**
   * When false/null, hide the scan-locations map (location not collected).
   */
  locationRequired?: boolean | null;
}

type AttendanceTimeSortKey = 'clockedIn' | 'clockedOut';
type SortDirection = 'asc' | 'desc';

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function parseTimestampMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function compareNullableTimes(
  aMs: number | null,
  bMs: number | null,
  direction: SortDirection,
): number {
  if (aMs == null && bMs == null) return 0;
  if (aMs == null) return 1;
  if (bMs == null) return -1;
  return direction === 'asc' ? aMs - bMs : bMs - aMs;
}

function isValidCoordinate(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat == null || lng == null) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function countMappableAttendees(members: AttendedEventMemberSummary[], mode: AttendanceMapMode): number {
  return members.filter((member) => {
    const lat = mode === 'clockIn' ? member.clockInLatitude : member.clockOutLatitude;
    const lng = mode === 'clockIn' ? member.clockInLongitude : member.clockOutLongitude;
    const timeScanned =
      mode === 'clockIn' ? member.clockedInAt || member.timeScanned : member.clockedOutAt;
    return Boolean(timeScanned) && isValidCoordinate(lat, lng);
  }).length;
}

const SessionAttendeesDialog: React.FC<SessionAttendeesDialogProps> = ({
  open,
  onClose,
  sessionId,
  sessionName,
  sessionDate,
  isAdmin = false,
  locationRequired = false,
}) => {
  const [members, setMembers] = useState<AttendedEventMemberSummary[]>([]);
  const [sessionLatitude, setSessionLatitude] = useState<number | null>(null);
  const [sessionLongitude, setSessionLongitude] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [mapMode, setMapMode] = useState<AttendanceMapMode>('clockIn');
  const [sortBy, setSortBy] = useState<AttendanceTimeSortKey>('clockedIn');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getSessionAttendedMembers(sessionId);
      setMembers(result.members);
      setSessionLatitude(result.sessionLatitude);
      setSessionLongitude(result.sessionLongitude);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load attendees');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && sessionId) {
      void load();
    } else {
      setMembers([]);
      setSessionLatitude(null);
      setSessionLongitude(null);
      setError(null);
      setMapMode('clockIn');
      setSortBy('clockedIn');
      setSortDirection('desc');
    }
  }, [open, sessionId, load]);

  const mappableCount = useMemo(
    () => countMappableAttendees(members, mapMode),
    [members, mapMode],
  );

  const hasClockOutAttendees = useMemo(
    () => members.some((member) => Boolean(member.clockedOutAt)),
    [members],
  );

  useEffect(() => {
    if (!hasClockOutAttendees) {
      setMapMode('clockIn');
    }
  }, [hasClockOutAttendees]);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const aMs =
        sortBy === 'clockedIn'
          ? parseTimestampMs(a.clockedInAt || a.timeScanned)
          : parseTimestampMs(a.clockedOutAt);
      const bMs =
        sortBy === 'clockedIn'
          ? parseTimestampMs(b.clockedInAt || b.timeScanned)
          : parseTimestampMs(b.clockedOutAt);
      return compareNullableTimes(aMs, bMs, sortDirection);
    });
  }, [members, sortBy, sortDirection]);

  const handleSort = (key: AttendanceTimeSortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection('desc');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Session Attendees
            </Typography>
            {sessionName && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {sessionName}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              {members.length} attendee{members.length === 1 ? '' : 's'}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
              disabled={!sessionId}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Edit Attendance
            </Button>
          </Stack>
          {loading && members.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No attendees have scanned in yet for this session.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {isAdmin && locationRequired && (
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Scan locations
                  </Typography>
                  {hasClockOutAttendees && (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={mapMode}
                      onChange={(_, value: AttendanceMapMode | null) => {
                        if (value) setMapMode(value);
                      }}
                    >
                      <ToggleButton value="clockIn">Clock in</ToggleButton>
                      <ToggleButton value="clockOut">Clock out</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Stack>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 280,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <SessionAttendeesMap
                    members={members}
                    mode={mapMode}
                    sessionLatitude={sessionLatitude}
                    sessionLongitude={sessionLongitude}
                  />
                  {mappableCount === 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        bgcolor: 'rgba(var(--color-surface-rgb, 250, 250, 250), 0.72)',
                        px: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" align="center">
                        No {mapMode === 'clockIn' ? 'clock-in' : 'clock-out'} locations recorded for this session.
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {mappableCount} location{mappableCount === 1 ? '' : 's'} plotted.
                  {isValidCoordinate(sessionLatitude, sessionLongitude)
                    ? ' Blue marker is the session QR location; blinking red markers are more than 50m away.'
                    : ' Click a marker for attendee details.'}
                </Typography>
              </Box>
              )}

              <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>CEA Number</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell sortDirection={sortBy === 'clockedIn' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'clockedIn'}
                        direction={sortBy === 'clockedIn' ? sortDirection : 'desc'}
                        onClick={() => handleSort('clockedIn')}
                      >
                        Clocked In
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'clockedOut' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'clockedOut'}
                        direction={sortBy === 'clockedOut' ? sortDirection : 'desc'}
                        onClick={() => handleSort('clockedOut')}
                      >
                        Clocked Out
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMembers.map((m) => (
                    <TableRow key={`${m.ceaNumber}`}>
                      <TableCell>{m.name || '—'}</TableCell>
                      <TableCell>{m.ceaNumber || '—'}</TableCell>
                      <TableCell>{m.mobile || '—'}</TableCell>
                      <TableCell>{formatTimestamp(m.clockedInAt || m.timeScanned)}</TableCell>
                      <TableCell>{formatTimestamp(m.clockedOutAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <SessionEditAttendanceDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        sessionId={sessionId}
        sessionDate={sessionDate}
        sessionName={sessionName}
        attendedMembers={members}
        onRefresh={load}
      />
    </>
  );
};

export default SessionAttendeesDialog;
